import { useState, useEffect, useRef } from "react";

interface UseDropdownOverflowTooltipParams {
  testId: string;
  selectedLabel?: string;
}

interface UseDropdownOverflowTooltipReturn {
  selectedValueRef: React.RefObject<HTMLDivElement>;
  dropdownOpen: boolean;
  isToggleOverflowing: boolean;
  selectKey: number;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelect: () => void;
  handleToggleButtonClick: () => void;
}

/**
 * Custom hook for managing dropdown overflow tooltips with icon
 * Handles overflow detection, dropdown state, and tooltip visibility
 */
export const useDropdownOverflowTooltip = ({
  testId,
  selectedLabel,
}: UseDropdownOverflowTooltipParams): UseDropdownOverflowTooltipReturn => {
  const selectedValueRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isToggleOverflowing, setIsToggleOverflowing] = useState(false);
  const [selectKey, setSelectKey] = useState(0);

  useEffect(() => {
    const toggleButtonTestId = `${testId}-toggle-button`;
    const el = document.querySelector(`[data-testid="${toggleButtonTestId}"]`) as HTMLElement | null;

    if (!el) return;

    const measure = () => {
      setIsToggleOverflowing(el.scrollWidth > el.clientWidth);
    };

    measure();
    const ResizeObserverConstructor = (window as any).ResizeObserver;
    const ro = ResizeObserverConstructor ? new ResizeObserverConstructor(measure) : null;

    if (ro && el) ro.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [selectedLabel, dropdownOpen, testId]);

  const handleSelect = () => {
    setDropdownOpen(false);
    // Force remount to clear any lingering tooltips
    setSelectKey((k) => k + 1);
  };

  const handleToggleButtonClick = () => {
    setDropdownOpen((v) => !v);
  };

  return {
    selectedValueRef,
    dropdownOpen,
    isToggleOverflowing,
    selectKey,
    setDropdownOpen,
    handleSelect,
    handleToggleButtonClick,
  };
};
